import { isPlainObj, isTerminateValue } from '@rehaje/shared'

import type { SchemaHelperTypes, WidgetHelperTypes } from './types'
import type { TypedSchema } from '@rehaje/core'
import type {
  QuerySpecific,
  TransformerSpecific,
  VariableSpecific
} from '@rehaje/lowcode'
import type { TRecord } from '@rehaje/shared'

export { javascript, mustache } from '@rehaje/lowcode'

const WidgetTypes = new WeakSet()

const upperStartCase = (key: string) => {
  return key.replace(/^[a-z]/, (char: string) => char.toUpperCase())
}

export const types: SchemaHelperTypes = new Proxy(
  {},
  {
    get(target, key) {
      if (typeof key === 'symbol') return
      const type = `Schema.${upperStartCase(key)}`
      return (...args: TypedSchema[]): TypedSchema => {
        let component
        const props: TRecord = {}
        args.forEach((arg: any) => {
          if (WidgetTypes.has(arg)) {
            component = {
              ...arg.component,
              children: arg.types ?? []
            }
          } else if (isPlainObj(arg)) {
            Object.assign(props, arg)
          }
        })
        const schema: TRecord = {}
        if (component) {
          schema.component = component
        }
        Object.assign(schema, props)
        schema.type = type
        if (schema.component?.type?.startsWith('Schema')) {
          schema.component = {
            ...schema.component.component,
            children: schema.component.types ?? []
          }
        }
        if (schema.decorator?.type?.startsWith('Schema')) {
          schema.decorator = {
            ...schema.decorator.component,
            children: schema.decorator.types ?? []
          }
        }
        return schema as any
      }
    }
  }
) as SchemaHelperTypes

export const widgets: WidgetHelperTypes = new Proxy(
  {},
  {
    get(target, key) {
      if (typeof key === 'symbol') return
      const type = `Widget.${upperStartCase(key)}`
      return (...args: any[]): TypedSchema => {
        const props: TRecord = {}
        const types: any[] = []
        args.forEach((arg) => {
          if (WidgetTypes.has(arg)) {
            types.push(arg)
          } else if (isPlainObj(arg) && !isTerminateValue(arg)) {
            Object.assign(props, arg)
          } else if (typeof arg === 'object') {
            types.push(arg)
          } else {
            types.push({
              code: String(arg),
              type: 'Mustache'
            })
          }
        })
        const schema: any = {
          component: {
            ...props,
            type
          },
          type: 'Schema.Void',
          types
        }
        if (
          schema.types.length === 0 &&
          schema.component.children?.length > 0
        ) {
          schema.types = schema.component.children
          delete schema.component.children
        }
        WidgetTypes.add(schema)
        return schema as any
      }
    }
  }
) as WidgetHelperTypes

export const states = {
  Query: (specific: Omit<QuerySpecific, 'type'>): QuerySpecific => {
    return {
      autoRefresh: true,
      ...specific,
      type: 'Query'
    }
  },
  Transformer: (
    specific: Omit<TransformerSpecific, 'type'>
  ): TransformerSpecific => {
    return {
      ...specific,
      type: 'Transformer'
    }
  },
  Variable: (specific: Omit<VariableSpecific, 'type'>): VariableSpecific => {
    return {
      ...specific,
      type: 'Variable'
    }
  }
}
